import type { NearbyPlace, SocialContext, UserKit } from "./types";
import { moodFromSlider } from "./moods";
import {
  FREE_KINDS,
  INDOOR_KINDS,
  OUTDOOR_KINDS,
  PAID_KINDS as CATALOG_PAID,
  PLACE_KIND_LABELS,
} from "./placeCatalog";

export type SettingPref = "any" | "indoor" | "outdoor";
export type TravelMode = "walk" | "run" | "bike" | "car";

export type PlanCard = {
  title: string;
  detail: string;
  minutes: number;
  price?: number;
  map?: { lat: number; lon: number };
  placeName?: string;
  /** Categorías de actividad del sitio (las rellena el motor de búsqueda). */
  categorias?: string[];
};

const PAID_KINDS = CATALOG_PAID;

export { INDOOR_KINDS, OUTDOOR_KINDS, PLACE_KIND_LABELS };
export const POI_KINDS = FREE_KINDS;

/** Qué encaja con cada compañía. */
export const SOCIAL_KINDS: Record<SocialContext, Set<string>> = {
  solo: new Set([
    "library",
    "cinema",
    "park",
    "plaza",
    "square",
    "shop",
    "bar",
    "cafe",
    "supermarket",
    "monument",
    "museum",
    "books",
    "viewpoint",
    "gallery",
    "bakery",
    "church",
    "castle",
    "attraction",
  ]),
  amigos: new Set([
    "bowling",
    "cinema",
    "bar",
    "restaurant",
    "nightclub",
    "shop",
    "plaza",
    "square",
    "monument",
    "cafe",
    "theatre",
    "park",
    "museum",
  ]),
  familia: new Set([
    "bowling",
    "cinema",
    "bar",
    "restaurant",
    "shop",
    "supermarket",
    "park",
    "plaza",
    "square",
    "monument",
    "museum",
    "bakery",
    "marketplace",
    "attraction",
    "castle",
  ]),
  familia_extendida: new Set([
    "bowling",
    "cinema",
    "restaurant",
    "park",
    "plaza",
    "square",
    "monument",
    "museum",
    "supermarket",
    "marketplace",
    "castle",
  ]),
  pareja: new Set([
    "restaurant",
    "shop",
    "bar",
    "bowling",
    "park",
    "plaza",
    "square",
    "monument",
    "cinema",
    "cafe",
    "museum",
    "gallery",
    "viewpoint",
    "castle",
  ]),
  colegas: new Set(["bar", "restaurant", "cafe", "bowling", "cinema", "plaza", "square"]),
  ninos: new Set(["park", "cinema", "museum", "plaza", "square", "bowling", "supermarket", "bakery", "attraction"]),
  mascotas: new Set(["park", "plaza", "square", "viewpoint", "monument"]),
  networking: new Set(["cafe", "bar", "restaurant", "library", "plaza", "square"]),
};

const KIND_TO_PHRASE: Record<string, (place: NearbyPlace, social: SocialContext) => string> = {
  cafe: (p) => titleNear(`Café en ${p.name}`, p),
  bar: (p, s) => titleNear(s === "solo" ? `Tomar algo en ${p.name}` : `Tomar algo en ${p.name}`, p),
  restaurant: (p) => titleNear(`Comer en ${p.name}`, p),
  museum: (p) => titleNear(`Visitar ${p.name}`, p),
  monument: (p) => titleNear(`Ver ${p.name}`, p),
  park: (p) => titleNear(`Pasear por ${p.name}`, p),
  library: (p) => titleNear(`Pasar por ${p.name}`, p),
  cinema: (p) => titleNear(`Cine en ${p.name}`, p),
  theatre: (p) => titleNear(`Teatro / cartelera en ${p.name}`, p),
  supermarket: (p) => titleNear(`Recado en ${p.name}`, p),
  plaza: (p, s) => titleNear(s === "solo" ? `Estar en ${p.name}` : `Quedar en ${p.name}`, p),
  square: (p, s) => titleNear(s === "solo" ? `Estar en ${p.name}` : `Quedar en ${p.name}`, p),
  books: (p) => titleNear(`Curiosear en ${p.name}`, p),
  bakery: (p) => titleNear(`Pasar por ${p.name}`, p),
  shop: (p) => titleNear(`Echar un vistazo en ${p.name}`, p),
  gallery: (p) => titleNear(`Visitar ${p.name}`, p),
  viewpoint: (p) => titleNear(`Mirador ${p.name}`, p),
  church: (p) => titleNear(`Visitar ${p.name}`, p),
  castle: (p) => titleNear(`Acercarte a ${p.name}`, p),
  attraction: (p) => titleNear(`Conocer ${p.name}`, p),
  artwork: (p) => titleNear(`Ver ${p.name}`, p),
  ruins: (p) => titleNear(`Explorar ${p.name}`, p),
  townhall: (p) => titleNear(`Pasar por ${p.name}`, p),
  pharmacy: (p) => titleNear(`Farmacia ${p.name}`, p),
  post: (p) => titleNear(`Correos ${p.name}`, p),
  laundry: (p) => titleNear(`Lavandería ${p.name}`, p),
  marketplace: (p) => titleNear(`Mercado ${p.name}`, p),
  bowling: (p) => titleNear(`Bolera ${p.name}`, p),
  sport: (p) => titleNear(`Actividad en ${p.name}`, p),
  nightclub: (p) => titleNear(`Salir en ${p.name}`, p),
  mountain: (p) => titleNear(`Subir a ${p.name}`, p),
  bridge: (p) => titleNear(`Cruzar ${p.name}`, p),
};

function dist(place: NearbyPlace) {
  if (place.distanceMeters == null) return "";
  if (place.distanceMeters < 1000) return ` (a ${place.distanceMeters} m)`;
  return ` (a ${(place.distanceMeters / 1000).toFixed(1).replace(".", ",")} km)`;
}

function who(social: SocialContext) {
  switch (social) {
    case "pareja":
      return "con tu pareja";
    case "amigos":
      return "con amigos";
    case "familia":
    case "familia_extendida":
      return "en familia";
    case "colegas":
      return "con colegas";
    case "ninos":
      return "con peques";
    case "mascotas":
      return "con tu mascota";
    case "networking":
      return "para conocer gente";
    default:
      return "";
  }
}

/** Título limpio: acción + sitio + distancia. Sin «por tu cuenta». */
function titleNear(action: string, place: NearbyPlace) {
  const base = `${action[0].toUpperCase()}${action.slice(1)}`;
  return `${base}${dist(place)}`;
}

function phrase(_social: SocialContext, action: string, place: NearbyPlace) {
  return titleNear(action, place);
}

function socialHint(social: SocialContext): string {
  const label = who(social);
  return label ? `Ideal ${label}.` : "";
}

function nightLifeText(text: string) {
  return /discoteca|club|fiesta|copas|nocturn|karaoke|pub crawl|salir de fiesta/i.test(text);
}

function groupOnlyText(text: string) {
  return /con amigos|con la pandilla|familiares|en grupo|quedada|quedar con/i.test(text);
}

export function placeAllowed(
  place: NearbyPlace,
  opts: { social: SocialContext; freeOnly?: boolean; setting?: SettingPref; strictSocial?: boolean },
): boolean {
  const kind = place.kind;
  if (opts.freeOnly && PAID_KINDS.has(kind)) return false;
  if (opts.setting === "indoor" && OUTDOOR_KINDS.has(kind) && !INDOOR_KINDS.has(kind)) return false;
  if (opts.setting === "outdoor" && INDOOR_KINDS.has(kind) && !OUTDOOR_KINDS.has(kind)) return false;
  if (opts.social === "solo") {
    if (kind === "nightclub" || nightLifeText(place.name)) return false;
  }
  const preferred = SOCIAL_KINDS[opts.social];
  if (opts.strictSocial !== false && preferred && !preferred.has(kind)) return false;
  return true;
}

export function filterPlaces(
  places: NearbyPlace[],
  opts: { social: SocialContext; freeOnly?: boolean; setting?: SettingPref; strictSocial?: boolean },
): NearbyPlace[] {
  return places.filter((p) => placeAllowed(p, opts));
}

/** Catálogo para pills y planes: relaja compañía, nunca interior/exterior. */
export function placesForPlan(
  places: NearbyPlace[],
  opts: { social: SocialContext; freeOnly?: boolean; setting?: SettingPref },
): NearbyPlace[] {
  const setting = opts.setting ?? "any";
  const tight = filterPlaces(places, { ...opts, setting, strictSocial: true });
  if (tight.length) return tight;
  const loose = filterPlaces(places, { ...opts, setting, strictSocial: false });
  if (loose.length) return loose;
  if (setting !== "any") return [];
  return places.slice(0, 40);
}

const OUTDOOR_TITLE_RE =
  /\b(parque|plaza|mirador|monumento|castillo|montaña|montana|puente|mercadillo|ruinas|pasear por|quedar en pla)/i;
const INDOOR_TITLE_RE =
  /\b(bar|café|cafe|restaurante|cine|bolera|discoteca|biblioteca|museo|supermercado|tienda|teatro|galería|galeria)\b/i;

export function cardFitsFilters(
  card: PlanCard,
  places: NearbyPlace[],
  opts: { social: SocialContext; freeOnly?: boolean; setting?: SettingPref },
): boolean {
  const setting = opts.setting ?? "any";
  const blob = `${card.title} ${card.detail} ${card.placeName || ""}`;
  if (opts.social === "solo" && (nightLifeText(blob) || groupOnlyText(blob))) return false;
  if (opts.freeOnly && card.price != null && card.price > 0) return false;
  const matched =
    places.find(
      (p) => card.placeName && p.name.toLowerCase() === card.placeName.toLowerCase(),
    ) || places.find((p) => card.title.toLowerCase().includes(p.name.toLowerCase()));
  if (matched) return placeAllowed(matched, { ...opts, setting });
  if (setting === "indoor" && OUTDOOR_TITLE_RE.test(blob) && !INDOOR_TITLE_RE.test(blob)) return false;
  if (setting === "outdoor" && INDOOR_TITLE_RE.test(blob) && !OUTDOOR_TITLE_RE.test(blob)) return false;
  return true;
}

/** Velocidades medias (km/h) — motor RadioMaximo = V × TiempoIda. */
export const SPEED_KMH: Record<TravelMode, number> = {
  walk: 4,
  run: 9,
  bike: 15,
  car: 60,
};

/** One-way target: round-trip ≤ 40% of free time → TiempoIda = (T×0.4)/2; meters = V×TiempoIda×1000. */
export function targetOneWayMeters(mode: TravelMode, totalMinutes: number): number {
  const tiempoLibreHoras = Math.max(0.25, totalMinutes / 60);
  const tiempoIdaHoras = (tiempoLibreHoras * 0.4) / 2;
  const v = SPEED_KMH[mode] ?? 4;
  return Math.round(Math.min(45000, Math.max(800, v * tiempoIdaHoras * 1000)));
}

const MODE_BASE_M: Record<TravelMode, number> = {
  walk: 850,
  run: 1300,
  bike: 2400,
  car: 8000,
};

const MODE_CAP_M: Record<TravelMode, number> = {
  walk: 12000,
  run: 20000,
  bike: 30000,
  car: 45000,
};

export function resolveTravelMode(opts: { hasCar?: boolean; mode?: TravelMode }): TravelMode {
  return opts.mode || (opts.hasCar ? "car" : "walk");
}

export function searchRadiusMeters(opts: {
  hasCar?: boolean;
  minutes?: number;
  mode?: TravelMode;
  stayLocal?: boolean;
}): number {
  const mode = resolveTravelMode(opts);
  if (opts.stayLocal) return stayLocalRadiusFor(mode);
  const minutes = opts.minutes ?? 90;
  return Math.round(Math.min(MODE_CAP_M[mode], Math.max(MODE_BASE_M[mode] * 0.5, targetOneWayMeters(mode, minutes))));
}

/** Radios crecientes: local → medio → amplio. Coche abre; bici y a pie cierran. */
export function cascadeRadiiMeters(opts: {
  hasCar?: boolean;
  minutes?: number;
  mode?: TravelMode;
  stayLocal?: boolean;
}): number[] {
  const mode = resolveTravelMode(opts);
  if (opts.stayLocal) return [stayLocalRadiusFor(mode)];
  const target = searchRadiusMeters({ ...opts, mode });
  if (mode === "car") {
    return Array.from(
      new Set([Math.min(3500, target), Math.min(9000, target), Math.min(18000, target), target].filter((n) => n > 0)),
    ).sort((a, b) => a - b);
  }
  if (mode === "bike") {
    return Array.from(new Set([Math.min(1600, target), Math.min(3500, target), target].filter((n) => n > 0))).sort(
      (a, b) => a - b,
    );
  }
  return Array.from(new Set([Math.min(900, target), Math.min(1600, target), target].filter((n) => n > 0))).sort(
    (a, b) => a - b,
  );
}

function stayLocalRadiusFor(mode: TravelMode): number {
  if (mode === "walk" || mode === "run") return 1400;
  if (mode === "bike") return 2200;
  return 3800;
}

function cardsFromPlaces(
  places: NearbyPlace[],
  input: {
    social: SocialContext;
    freeOnly?: boolean;
    moodLabel: string;
  },
): PlanCard[] {
  return places
    .slice()
    .sort((a, b) => {
      const preferred = SOCIAL_KINDS[input.social];
      const sa = preferred?.has(a.kind) ? 0 : 1;
      const sb = preferred?.has(b.kind) ? 0 : 1;
      if (sa !== sb) return sa - sb;
      return (a.distanceMeters || 0) - (b.distanceMeters || 0);
    })
    .slice(0, 40)
    .map((place) => {
      const builder =
        KIND_TO_PHRASE[place.kind] ||
        ((p: NearbyPlace, s: SocialContext) => phrase(s, `conocer ${p.name}`, p));
      const title = builder(place, input.social);
      const walkMin = Math.max(8, Math.round((place.distanceMeters || 800) / 80));
      const where = place.address ? `Está en ${place.address}` : `${place.name} está cerca`;
      const hint = socialHint(input.social);
      return {
        title,
        detail: [input.moodLabel, hint, where, input.freeOnly ? "0 €" : ""]
          .filter(Boolean)
          .join(" · "),
        minutes: Math.min(120, walkMin + 25),
        map: { lat: place.latitude, lon: place.longitude },
        placeName: place.name,
        price: input.freeOnly ? 0 : undefined,
      } satisfies PlanCard;
    });
}

export function recommendPlans(input: {
  mood: number;
  social: SocialContext;
  rainy?: boolean;
  freeOnly?: boolean;
  setting?: SettingPref;
  places: NearbyPlace[];
  kit: UserKit;
  hour?: number;
}): PlanCard[] {
  const mood = moodFromSlider(input.mood);
  const setting: SettingPref = input.setting ?? (input.rainy ? "indoor" : "any");

  const places = placesForPlan(input.places, {
    social: input.social,
    freeOnly: input.freeOnly,
    setting,
  });

  const fromPlaces = cardsFromPlaces(places, {
    social: input.social,
    freeOnly: input.freeOnly,
    moodLabel: mood.label,
  });

  const hobbyHits: PlanCard[] = [];
  for (const hobby of input.kit.hobbies.slice(0, 12)) {
    if (input.social === "solo" && groupOnlyText(hobby)) continue;
    if (/bici|ciclismo/.test(hobby) && input.kit.gear.includes("bicicleta") && setting !== "indoor") {
      const park = places.find((p) => p.kind === "park");
      if (park) {
        hobbyHits.push({
          title: `Salir en bici hacia ${park.name}${dist(park)}`,
          detail: `Tienes bici anotada. ${mood.hint}`,
          minutes: 50,
          map: { lat: park.latitude, lon: park.longitude },
          placeName: park.name,
        });
        continue;
      }
    }
    if (/ajedrez/.test(hobby) && !input.freeOnly) {
      const cafe = places.find((p) => p.kind === "cafe");
      if (cafe) {
        hobbyHits.push({
          title: `Partida de ajedrez en ${cafe.name}${dist(cafe)}`,
          detail: "Lleva el tablero si lo tienes en el perfil.",
          minutes: 60,
          map: { lat: cafe.latitude, lon: cafe.longitude },
          placeName: cafe.name,
        });
        continue;
      }
    }
    if (/museo/.test(hobby) && setting !== "outdoor") {
      const museum = places.find((p) => p.kind === "museum");
      if (museum) {
        hobbyHits.push({
          title: `Visitar ${museum.name}${dist(museum)}`,
          detail: hobby,
          minutes: 90,
          map: { lat: museum.latitude, lon: museum.longitude },
          placeName: museum.name,
          price: input.freeOnly ? 0 : undefined,
        });
        continue;
      }
    }
    const near = places[0];
    if (near) {
      hobbyHits.push({
        title: `Encajar “${hobby}” cerca de ${near.name}${dist(near)}`,
        detail: mood.hint,
        minutes: 40,
        map: { lat: near.latitude, lon: near.longitude },
        placeName: near.name,
      });
    }
  }

  const errands = places
    .filter((p) =>
      ["supermarket", "bakery", "pharmacy", "post", "laundry", "shop", "marketplace"].includes(p.kind),
    )
    .slice(0, 8)
    .map((place) => {
      const builder = KIND_TO_PHRASE[place.kind] || KIND_TO_PHRASE.shop;
      return {
        title: builder(place, input.social),
        detail: `Quehacer cerca de ti${place.address ? ` · ${place.address}` : ""} · pulsa para abrir Google Maps`,
        minutes: Math.min(50, Math.max(12, Math.round((place.distanceMeters || 600) / 70))),
        map: { lat: place.latitude, lon: place.longitude },
        placeName: place.name,
        price: input.freeOnly ? 0 : undefined,
      } satisfies PlanCard;
    });

  const withMap = [...fromPlaces, ...errands, ...hobbyHits].filter((item) => item.map && item.placeName);
  const activityOnly = activityFallbacks({
    social: input.social,
    moodLabel: mood.label,
    rainy: input.rainy,
    kit: input.kit,
    setting,
  });
  const extras = withMap.length >= 4 ? [] : activityOnly.slice(0, withMap.length ? 2 : 6);
  const merged = withMap.length ? [...withMap, ...extras] : [...fromPlaces, ...activityOnly];
  const uniq = new Map<string, PlanCard>();
  const filterOpts = { social: input.social, freeOnly: input.freeOnly, setting };
  for (const item of merged) {
    if (!cardFitsFilters(item, input.places, filterOpts)) continue;
    uniq.set(item.title, item);
  }
  if (!uniq.size && places.length) {
    for (const card of cardsFromPlaces(places.slice(0, 24), {
      social: input.social,
      freeOnly: false,
      moodLabel: mood.label,
    })) {
      if (cardFitsFilters(card, places, filterOpts)) uniq.set(card.title, card);
    }
  }
  if (!uniq.size) {
    for (const card of activityOnly) {
      if (cardFitsFilters(card, places, filterOpts)) uniq.set(card.title, card);
    }
  }
  return [...uniq.values()].slice(0, 24);
}

function activityFallbacks(input: {
  social: SocialContext;
  moodLabel: string;
  rainy?: boolean;
  kit: UserKit;
  setting?: SettingPref;
}): PlanCard[] {
  const hint = socialHint(input.social);
  // La lluvia empuja a interior solo si el usuario no ha pedido exteriores a mano.
  const outdoor = input.setting === "outdoor";
  const indoor = input.setting === "indoor" || (Boolean(input.rainy) && !outdoor);
  const cards: PlanCard[] = [];
  if (!outdoor) {
    cards.push({
      title: "Estiramientos o ejercicio suave en casa",
      detail: [input.moodLabel, hint, "Plan de interior"].filter(Boolean).join(" · "),
      minutes: 20,
      price: 0,
    });
    cards.push({
      title: "Leer 25 minutos sin pantalla",
      detail: [hint, "Biblioteca en casa o un ebook"].filter(Boolean).join(" · "),
      minutes: 25,
      price: 0,
    });
    cards.push({
      title: "Cocinar algo sencillo o un café especial",
      detail: [input.moodLabel, hint, "En casa"].filter(Boolean).join(" · "),
      minutes: 40,
      price: 0,
    });
  }
  if (!indoor) {
    cards.push({
      title: "Paseo de 25–40 min por el barrio",
      detail: [input.moodLabel, hint, "Sin depender del mapa"].filter(Boolean).join(" · "),
      minutes: 35,
      price: 0,
    });
    cards.push({
      title: "Explorar 3 calles que no suelas cruzar",
      detail: [input.moodLabel, hint, "Plan de barrio"].filter(Boolean).join(" · "),
      minutes: 40,
      price: 0,
    });
    cards.push({
      title: "Lista de 10 fotos de detalles urbanos",
      detail: [hint, "Fachadas, escaparates, árboles"].filter(Boolean).join(" · "),
      minutes: 30,
      price: 0,
    });
  }
  if (
    input.kit.hobbies.some((h) => /bici|ciclismo/i.test(h)) &&
    input.kit.gear.includes("bicicleta") &&
    !indoor
  ) {
    cards.unshift({
      title: "Vuelta en bici de 40–60 min",
      detail: [hint, "Tienes bici en el kit"].filter(Boolean).join(" · "),
      minutes: 50,
      price: 0,
    });
  }
  if (input.kit.hobbies.some((h) => /leer|lectura|libro/i.test(h))) {
    cards.push({
      title: "Leer un capítulo del libro que tienes a medias",
      detail: [hint, indoor ? "En casa" : "En casa o en un banco"].filter(Boolean).join(" · "),
      minutes: 30,
      price: 0,
    });
  }
  return cards;
}
